import { useTranslation } from "react-i18next";
import React, { useState, useMemo, useCallback } from "react";
import { Layout } from "@/components/layout";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Search, Plus, FileText, Send, Eye, List } from "lucide-react";
import { formatDate } from "@/lib/date-utils";
import { useToast } from "@/hooks/use-toast";
import { toastApiError } from "@/lib/error-messages";
import {
  STATUS_COLORS,
  quoteStatusEnum,
} from "@shared/schema";
import { useStatusLabel } from "@/lib/use-status-label";
import { useEnhancedFilteredList } from "@/hooks/use-fuzzy-search";
import { useLocation } from "wouter";
import { SendForApprovalConfirmDialog } from "@/components/SendForApprovalConfirmDialog";
import { NoEmailErrorDialog } from "@/components/NoEmailErrorDialog";
import { apiRequest } from "@/lib/queryClient";
import { ToastAction } from "@/components/ui/toast";
import { formatCentsAsDollars } from "@/lib/price-utils";
import { getTaxAmount, shouldDisplayTax } from "@/lib/tax-utils";
import { getCustomerDisplayName } from "@shared/customer-utils";

// Import our new abstractions
import { QuoteViewer } from "@/features/quotes/QuoteViewer";
import { Dialog, DialogContent } from "@/components/ui/dialog";
// Removed NewQuoteContainer - now handled by separate /quotes/new page
import useQuotes from "@/hooks/use-quotes";
import { useProductsLookup } from "@/hooks/use-products";
import { useServices } from "@/hooks/use-services";
import { useCustomParts } from "@/hooks/use-custom-parts";
import { quoteToPricingInput } from "@shared/pricing-utils";
import { PricingService } from "@shared/pricing-service";
import { getCustomers } from "@/requests/customers/get-customers";

// Status colors now imported from centralized schema

// Component for the quotes list view - moved outside to prevent re-creation
const QuotesListView = React.memo(
  ({
    quotes,
    isLoading,
    onCreateNew,
    onViewQuote,
    onSendForApproval,
    filterTabs,
    activeTab,
    onTabChange,
    searchQuery,
    onSearchChange,
    getKey,
    sendingQuoteId,
  }: {
    quotes: any[];
    isLoading: boolean;
    onCreateNew: () => void;
    onViewQuote: (quote: any) => void;
    onSendForApproval: (quote: any) => void;
    filterTabs?: any[];
    activeTab?: string;
    onTabChange?: (tab: string) => void;
    searchQuery?: string;
    onSearchChange?: (query: string) => void;
    getKey?: (item: any, index: number) => string | number;
    sendingQuoteId?: string | null;
  }) => {
    const { t } = useTranslation();
    const formatQuoteStatus = useStatusLabel("quote");
    return (
      <div className="space-y-6">
        {/* Create button */}
        <div className="flex justify-end">
          <Button onClick={onCreateNew} size="lg" className="h-12 text-base">
            <Plus className="h-5 w-5 mr-2" />
            {t("pages.quotes.newQuote")}
          </Button>
        </div>

        {/* Filter Tabs */}
        {filterTabs && filterTabs.length > 0 && (
          <div className="w-full">
            <Tabs
              value={activeTab}
              onValueChange={onTabChange}
              className="w-full"
            >
              <TabsList className="w-full justify-evenly h-auto min-h-10 py-1">
                {filterTabs.map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="flex-1 whitespace-normal text-xs sm:text-sm min-w-0 px-1.5 sm:px-3"
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        )}

        {/* Search Input */}
        {searchQuery !== undefined && onSearchChange && (
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              type="text"
              placeholder={t("pages.quotes.searchQuotesCustomersOr")}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-10 w-full h-12 text-base"
            />
          </div>
        )}

        {/* Cards Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="bg-card p-6 rounded-lg border animate-pulse"
              >
                <div className="h-6 bg-muted rounded mb-3"></div>
                <div className="space-y-2 mb-4">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-4 bg-muted rounded w-1/2"></div>
                </div>
                <div className="h-6 bg-muted rounded w-20 mb-2"></div>
                <div className="h-4 bg-muted rounded w-32 mb-4"></div>
                <div className="h-10 bg-muted rounded"></div>
              </div>
            ))}
          </div>
        ) : quotes && quotes.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {quotes.map((quote: any) => {
              // customerName is now pre-computed from quotesWithCustomers

              return (
                <div
                  key={getKey ? getKey(quote, 0) : quote.uid}
                  data-testid="quote-card"
                  className="bg-card p-6 rounded-lg border hover:shadow-md transition-shadow group"
                >
                  <div className="flex flex-col h-full">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2 gap-3">
                        <h3 className="font-semibold text-lg group-hover:text-primary transition-colors flex-1 min-w-0 truncate">
                          {quote.title}
                        </h3>
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white shrink-0 whitespace-nowrap ${STATUS_COLORS[quote.quoteStatus as keyof typeof STATUS_COLORS] || "bg-gray-500"}`}
                        >
                          {quote.quoteStatus
                            ? formatQuoteStatus(quote.quoteStatus)
                            : t("pages.quotes.noStatus")}
                        </span>
                      </div>

                      {quote.description && (
                        <p className="text-sm text-muted-foreground mb-3">
                          {quote.description}
                        </p>
                      )}

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            {t("pages.quotes.total")}
                          </span>
                          <span className="font-semibold text-lg">
                            $
                            {formatCentsAsDollars(
                              quote.displayAmount ??
                                quote.amountPostsalesTax ??
                                quote.amountPresalesTax,
                            )}
                          </span>
                        </div>

                        {shouldDisplayTax(quote.taxAmount) && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">
                              {t("pages.quotes.salesTaxEstimate")}
                            </span>
                            <span className="text-sm">
                              $
                              {formatCentsAsDollars(
                                getTaxAmount(quote.taxAmount),
                              )}
                            </span>
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            {t("pages.quotes.customer")}
                          </span>
                          <span className="text-sm font-medium">
                            {quote.customerName}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            {t("pages.quotes.createdOn")}
                          </span>
                          <span className="text-sm">
                            {formatDate(quote.creationTime)}
                          </span>
                        </div>

                        {quote.createdByName && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">
                              {t("pages.quotes.createdBy")}
                            </span>
                            <span className="text-sm">
                              {quote.createdByName}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      {/* Show Send for Approval button if quote is ready to send */}
                      {quote.quoteStatus === "ready_to_send" && (
                        <Button
                          className="w-full h-12 text-base"
                          size="lg"
                          onClick={() => onSendForApproval(quote)}
                          disabled={sendingQuoteId === quote.uid}
                        >
                          <Send className="h-4 w-4 mr-2" />
                          {sendingQuoteId === quote.uid
                            ? t("pages.quotes.sending")
                            : t("pages.quotes.sendForApproval")}
                        </Button>
                      )}

                      {/* Show Schedule as Job button if quote is accepted */}
                      {/* {quote.quoteStatus === 'accepted' && (
                      <Button
                        className="w-full h-12 text-base"
                        size="lg"
                        onClick={() => onScheduleAsJob(quote)}
                      >
                        <Calendar className="h-4 w-4 mr-2" />
                        Schedule as Job
                      </Button>
                    )} */}

                      <Button
                        className="w-full h-12 text-base"
                        size="lg"
                        variant="outline"
                        data-testid="view-quote-button"
                        onClick={() => onViewQuote(quote)}
                      >
                        {t("pages.quotes.viewQuote")}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="flex flex-col items-center">
              <FileText className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="font-medium text-lg mb-2">{t("pages.quotes.noQuotesFound")}</h3>
              <p className="text-muted-foreground mb-6">
                {t("pages.quotes.createYourFirstQuote")}
              </p>
              <Button onClick={onCreateNew} size="lg">
                <Plus className="h-5 w-5 mr-2" />
                {t("pages.quotes.newQuote")}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  },
);

export default function Quotes() {
  const { t } = useTranslation();
  const formatQuoteStatus = useStatusLabel("quote");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();

  // UI state
  const [viewQuoteData, setViewQuoteData] = useState<any>(null);
  const [viewQuoteDialogOpen, setViewQuoteDialogOpen] = useState(false);
  // Toggle for customer view (hides parts breakdown) vs detailed view
  const [showDetailedView, setShowDetailedView] = useState(false);
  // Confirmation dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [quoteToSend, setQuoteToSend] = useState<any>(null);
  // No email error dialog state
  const [noEmailDialogOpen, setNoEmailDialogOpen] = useState(false);
  const [quoteWithNoEmail, setQuoteWithNoEmail] = useState<any>(null);
  // Filter and search state
  const [activeStatusTab, setActiveStatusTab] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return decodeURIComponent(urlParams.get("search") || "");
  });

  // Initialize templateId from URL
  // Template handling moved to /quotes/new page

  const { data: customers, isLoading: customersLoading } =
    useQuery(getCustomers());

  const { quotes, isLoading: quotesLoading } = useQuotes();

  // Get services and custom parts for pricing calculations
  const {
    services,
    isLoading: servicesLoading,
    error: servicesError,
  } = useServices();
  const {
    customParts,
    isLoading: customPartsLoading,
    error: customPartsError,
  } = useCustomParts();

  // Extract product IDs from viewed quote for lookup
  const viewedQuoteProductIds = useMemo(() => {
    if (!viewQuoteData?.products) return [];
    return viewQuoteData.products.map((p: any) => p.productId).filter(Boolean);
  }, [viewQuoteData]);

  // Lookup products for quote viewing
  const {
    products,
    isLoading: productsLoading,
    error: productsError,
  } = useProductsLookup(
    viewedQuoteProductIds,
    viewedQuoteProductIds.length > 0,
  );

  // Filter tabs configuration for quotes - programmatically generated from schema
  // Note: Filtering out 'scheduled_as_job' since job functionality is currently disabled
  const quoteFilterTabs = useMemo(
    () => [
      { label: t("pages.quotes.all"), value: "all" },
      ...quoteStatusEnum.enumValues
        .filter((status) => status !== "scheduled_as_job")
        .map((status) => ({
          label: formatQuoteStatus(status),
          value: status,
        })),
    ],
    [t, formatQuoteStatus],
  );

  const handleCreateNew = useCallback(() => {
    navigate("/quotes/new");
  }, [navigate]);

  const handleViewQuote = useCallback((quote: any) => {
    setViewQuoteData(quote);
    setShowDetailedView(false); // Default to customer view (hides parts breakdown)
    setViewQuoteDialogOpen(true);
  }, []);

  const [sendingQuoteId, setSendingQuoteId] = useState<string | null>(null);

  const sendForApproval = useMutation({
    mutationFn: async (quoteId: string) => {
      setSendingQuoteId(quoteId);
      const response = await apiRequest(
        "POST",
        `/api/quotes/${quoteId}/send-for-approval`,
      );
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error);
      }
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      setSendingQuoteId(null);

      // Construct approval link if we have the necessary data
      const approvalLink =
        data.quoteNumber && data.approvalCode
          ? `/quote-approval/${data.quoteNumber}?code=${data.approvalCode}`
          : null;

      toast({
        title: t("pages.quotes.quoteSentForApproval"),
        description:
          t("pages.quotes.theCustomerApprovalLink"),
        action: approvalLink ? (
          <ToastAction
            altText={t("pages.quotes.viewApprovalLink")}
            onClick={() => {
              window.open(approvalLink, "_blank", "noopener,noreferrer");
            }}
          >
            {t("pages.quotes.viewLink")}
          </ToastAction>
        ) : undefined,
      });
    },
    onError: (error: any) => {
      console.error("Error sending quote for approval:", error);
      setSendingQuoteId(null);
      toastApiError(toast, error, t("pages.quotes.failedToSendQuote"));
    },
  });

  const handleSendForApproval = useCallback(
    (quote: any) => {
      if (sendingQuoteId) return; // Prevent multiple clicks

      // Check if customer has an email
      const customer = customers?.find((c) => c.uid === quote.customerId);
      const customerEmail = customer?.email;

      // If no email or empty email, show error dialog
      if (!customerEmail || customerEmail.trim() === "") {
        setQuoteWithNoEmail(quote);
        setNoEmailDialogOpen(true);
        return;
      }

      // Customer has email, proceed with confirmation dialog
      setQuoteToSend(quote);
      setConfirmDialogOpen(true);
    },
    [sendingQuoteId, customers],
  );

  const handleConfirmSendForApproval = useCallback(() => {
    if (quoteToSend) {
      sendForApproval.mutate(quoteToSend.uid);
      setConfirmDialogOpen(false);
      setQuoteToSend(null);
    }
  }, [sendForApproval, quoteToSend]);

  // const handleScheduleAsJob = useCallback((quote: any) => {
  //   // Navigate to jobs page with the quote pre-selected
  //   navigate(`/jobs?createJob=true&quoteNumber=${quote.quoteNumber}`);
  // }, [navigate]);

  const handleTabChange = useCallback((tab: string) => {
    setActiveStatusTab(tab);
  }, []);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const getQuoteKey = useCallback((quote: any, index: number) => {
    return quote.uid || `quote-${index}`;
  }, []);

  // Extract product IDs from all draft quotes for pricing lookup
  const draftQuoteProductIds = useMemo(() => {
    if (!quotes) return [];

    const draftQuotes = quotes.filter((q) => !q.pricesLockedAt);
    const productIds = new Set<string>();

    draftQuotes.forEach((quote) => {
      if (Array.isArray(quote.products)) {
        quote.products.forEach((p: any) => {
          if (p.productId) productIds.add(p.productId);
        });
      }
    });

    return Array.from(productIds);
  }, [quotes]);

  // Fetch products for draft quote pricing
  const { products: draftProducts, isLoading: draftProductsLoading } =
    useProductsLookup(draftQuoteProductIds, draftQuoteProductIds.length > 0);

  // Create product map for O(1) lookup
  const draftProductsMap = useMemo(() => {
    if (!draftProducts) return new Map();
    return new Map(draftProducts.map((p) => [p.uid, p]));
  }, [draftProducts]);

  // Enhanced quotes with customer names AND recalculated amounts for drafts
  const quotesWithDisplayAmounts = useMemo(() => {
    if (!quotes || !customers) return [];

    return quotes
      .map((quote) => {
        const customer = customers.find((c: any) => c.uid === quote.customerId);
        let displayAmount = quote.amountPostsalesTax ?? quote.amountPresalesTax;

        // For draft quotes: recalculate total with current prices using PricingService
        // Wait for all data to load before calculating to avoid missing services/custom parts
        // Also ensure services and customParts are actually populated (not just finished loading)
        if (
          !quote.pricesLockedAt &&
          !draftProductsLoading &&
          !servicesLoading &&
          !customPartsLoading &&
          services &&
          customParts
        ) {
          // Use quoteToPricingInput helper for consistent transformation
          const pricingInput = quoteToPricingInput(
            quote,
            Array.from(draftProductsMap.values()),
            services,
            customParts,
          );

          // Calculate using PricingService for consistency with QuoteEditor and QuoteViewer
          const pricingResult = PricingService.calculatePricing(pricingInput);

          // Tax is still historical (immutable) - always use stored taxAmount
          const taxAmount = quote.taxAmount || 0;
          displayAmount = pricingResult.subtotalAfterDiscount + taxAmount;
        }

        return {
          ...quote,
          customerName: getCustomerDisplayName(customer),
          displayAmount, // Add computed display amount
        };
      })
      .sort((a, b) => {
        // Sort by creation time, newest first
        const timeA = a.creationTime ? new Date(a.creationTime).getTime() : 0;
        const timeB = b.creationTime ? new Date(b.creationTime).getTime() : 0;
        return timeB - timeA;
      });
  }, [
    quotes,
    customers,
    draftProductsMap,
    draftProductsLoading,
    services,
    customParts,
    servicesLoading,
    customPartsLoading,
  ]);

  const filteredQuotes = useEnhancedFilteredList({
    items: quotesWithDisplayAmounts,
    activeTab: activeStatusTab,
    searchQuery,
    statusKey: "quoteStatus",
    searchKeys: ["title", "description", "notes", "customerName"],
    customSearchFn: (quote: any, query: string) => {
      // Fallback exact matching if fuzzy search fails
      return (
        quote.title?.toLowerCase().includes(query) ||
        quote.description?.toLowerCase().includes(query) ||
        quote.notes?.toLowerCase().includes(query) ||
        quote.customerName.toLowerCase().includes(query)
      );
    },
  });

  return (
    <Layout headerContent={<span className="font-semibold">{t("pages.quotes.quotes")}</span>}>
      <div className="space-y-6">
        {/* Quotes List View - New Quote Form moved to /quotes/new */}
        <QuotesListView
          quotes={filteredQuotes}
          isLoading={customersLoading || quotesLoading}
          onCreateNew={handleCreateNew}
          onViewQuote={handleViewQuote}
          onSendForApproval={handleSendForApproval}
          filterTabs={quoteFilterTabs}
          activeTab={activeStatusTab}
          onTabChange={handleTabChange}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          getKey={getQuoteKey}
          sendingQuoteId={sendingQuoteId}
        />

        {/* View Quote Dialog */}
        {viewQuoteData && (
          <Dialog
            open={viewQuoteDialogOpen}
            onOpenChange={setViewQuoteDialogOpen}
          >
            <DialogContent
              className="sm:max-w-[900px] max-h-[90vh] p-0 flex flex-col"
              hideCloseButton
            >
              {/* View Mode Selector Header */}
              <div className="border-b bg-muted/30 px-6 py-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h3 className="font-medium text-foreground">{t("pages.quotes.previewMode")}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t("pages.quotes.customerViewHidesParts")}
                    </p>
                  </div>
                  <ToggleGroup
                    type="single"
                    value={showDetailedView ? "detailed" : "customer"}
                    onValueChange={(value) => {
                      if (value) setShowDetailedView(value === "detailed");
                    }}
                    className="bg-muted rounded-lg p-1"
                  >
                    <ToggleGroupItem
                      value="customer"
                      aria-label={t("pages.quotes.customerView")}
                      className="px-4 py-2 text-sm data-[state=on]:bg-background data-[state=on]:shadow-sm rounded-md"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      {t("pages.quotes.customerView_2")}
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      value="detailed"
                      aria-label={t("pages.quotes.detailedView")}
                      className="px-4 py-2 text-sm data-[state=on]:bg-background data-[state=on]:shadow-sm rounded-md"
                    >
                      <List className="h-4 w-4 mr-2" />
                      {t("pages.quotes.detailedView_2")}
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                <QuoteViewer
                  data={viewQuoteData}
                  viewMode="internal"
                  customer={customers?.find(
                    (c) => c.uid === viewQuoteData.customerId,
                  )}
                  products={products}
                  services={services || []}
                  customParts={customParts || []}
                  productsLoading={productsLoading}
                  servicesLoading={servicesLoading}
                  customPartsLoading={customPartsLoading}
                  productsError={productsError}
                  servicesError={servicesError as Error | null}
                  customPartsError={customPartsError as Error | null}
                  hidePartDetails={!showDetailedView}
                />
              </div>
              <div className="border-t bg-background px-6 py-4 flex justify-end">
                <Button
                  onClick={() => setViewQuoteDialogOpen(false)}
                  size="lg"
                  className="min-w-[120px]"
                >
                  {t("common.close")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* No Email Error Dialog */}
        {quoteWithNoEmail && (
          <NoEmailErrorDialog
            open={noEmailDialogOpen}
            onOpenChange={setNoEmailDialogOpen}
            customerName={getCustomerDisplayName(
              customers?.find((c) => c.uid === quoteWithNoEmail.customerId),
            )}
          />
        )}

        {/* Send for Approval Confirmation Dialog */}
        {quoteToSend && (
          <SendForApprovalConfirmDialog
            open={confirmDialogOpen}
            onOpenChange={setConfirmDialogOpen}
            onConfirm={handleConfirmSendForApproval}
            customerEmail={
              customers?.find((c) => c.uid === quoteToSend.customerId)?.email ||
              t("common.unknown")
            }
            customerName={getCustomerDisplayName(
              customers?.find((c) => c.uid === quoteToSend.customerId),
            )}
            quoteNumber={quoteToSend.quoteNumber}
            isLoading={sendingQuoteId === quoteToSend.uid}
          />
        )}
      </div>
    </Layout>
  );
}
